import { InputActionMeta, SelectField, SelectFieldAsyncProps, SelectFieldRef } from '@almbrand/selectfield';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import { debounce } from 'utilities';
import {
	getSearchComponent,
	SearchComponent,
	SearchConfiguration,
} from '../../../searchConfiguration/SearchConfiguration';
import { SpdSearch } from '../../../searchConfiguration/SpdSearch';

type SelectActionType =
	| 'clear'
	| 'create-option'
	| 'deselect-option'
	| 'pop-value'
	| 'remove-value'
	| 'select-option'
	| 'set-value';

export interface SearchFieldOption {
	label: string;
	value: string;
}

type SearchFieldComponentProps = SelectFieldAsyncProps<SearchFieldOption> & {
	register: UseFormRegister<FieldValues>;
	contentType: string;
	stepContentId: string;
	submitStep?: () => void;
	placeholderText?: string;
	searchConfiguration?: SearchConfiguration;
	secondarySearchConfiguration?: SearchConfiguration;
	stepTitle?: string;
	setValue?: UseFormSetValue<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	iconPosition?: 'right' | 'left';
	icon?: Icon;
	autocomplete?: React.HTMLInputAutoCompleteAttribute;
	debounced?: boolean;
};

// TODO update SearchField in component library with debounce functionality, then remove from this
export const SearchFieldComponent: React.FC<SearchFieldComponentProps> = React.memo(
	({
		register,
		submitStep,
		contentType,
		stepContentId,
		searchConfiguration,
		secondarySearchConfiguration,
		isSearchable = true,
		placeholderText,
		setValue,
		trigger,
		iconPosition = 'right',
		icon,
		autocomplete = 'off',
		debounced = true,
		...props
	}) => {
		const pageContext = useContext(PageContext);
		const { theme } = pageContext ?? {};
		const themeName = ThemeNames[theme];

		const formState = useAppSelector((state) => state.calculatorForm);

		// Register field with react-hook-form
		const { onChange, name } = register(stepContentId.toString(), { required: true });

		// State for the selected value(s)
		const [searchResultValue, setSearchResultValue] = useState<readonly SearchFieldOption[]>();
		// State for the search component logic
		const [searchComponent, setSearchComponent] = useState<SearchComponent>();
		// State to control rendering after async setup
		const [renderField, setRenderField] = useState(false);
		// State for menu open/close (for async search)
		const [handleMenu, setHandleMenu] = useState<boolean | undefined>(undefined);
		// State for focus
		const [isFocus, setIsFocus] = useState(false);
		// State to track if field has been changed/touched
		const [isTouched, setIsTouched] = useState(false);
		// Controlled input value for cascade searches
		const [inputValue, setInputValue] = useState('');
		// Tracks whether a cascade search flow is in progress
		const [isCascadeFlow, setIsCascadeFlow] = useState(false);
		// Preloaded options for the next cascade step
		const [prefetchedOptions, setPrefetchedOptions] = useState<SearchFieldOption[] | undefined>(undefined);
		// Forces SelectField to resync its internal async state during cascade
		const [asyncKey, setAsyncKey] = useState(0);

		const selectRef = useRef<SelectFieldRef>(null);
		const keepMenuOpenAfterSelectRef = useRef(false);
		const suppressNextLoadRef = useRef(false);

		// Set placeholder from prop (fix for CMS naming)
		props.placeHolder = placeholderText;

		// Initialize search component and render flag on mount or when searchConfiguration changes
		useEffect(() => {
			setSearchComponent(getSearchComponent(searchConfiguration, pageContext, secondarySearchConfiguration));
			setRenderField(true);
		}, [searchConfiguration, secondarySearchConfiguration]);

		// // Trigger focus event on input to detect browser autofill

		// Filter options by label or value (case-insensitive)
		const filterValues = useCallback((inputValue: string, options: SearchFieldOption[]): SearchFieldOption[] => {
			const lowerInput = inputValue.toLowerCase();
			return options.filter(
				(option) =>
					option.label.toLowerCase().includes(lowerInput) || option.value.toLowerCase().includes(lowerInput)
			);
		}, []);

		const updateSearchResultValueIfNeeded = (
			filtered: SearchFieldOption[],
			searchResultValue: readonly SearchFieldOption[] | undefined,
			setSearchResultValue: (v: SearchFieldOption[]) => void
		) => {
			if (
				(searchResultValue?.length ?? 0) !== filtered.length ||
				filtered.some((item, i) => {
					const current = searchResultValue?.[i];
					return current?.value !== item.value || current?.label !== item.label;
				})
			) {
				setSearchResultValue(filtered);
			}
		};

		// This function loads automatically on render to get async options for SelectField
		const handleAsyncOptions = useCallback(
			async (inputValue: string): Promise<SearchFieldOption[]> => {
				if (suppressNextLoadRef.current) {
					suppressNextLoadRef.current = false;
					return [];
				}

				if (!searchComponent) return [];

				const toLovOptions = (result: SearchFieldOption[] | Record<string, unknown> | undefined) =>
					Array.isArray(result) ? result : [];

				const formData = formState[name]?.data;

				// Helper for updating and returning filtered options
				const processOptions = async (options: SearchFieldOption[] | undefined, filterInput: string) => {
					if (!options) return [];
					const filtered = searchComponent.apiSearch ? options : filterValues(filterInput, options);
					updateSearchResultValueIfNeeded(filtered, searchResultValue, setSearchResultValue);
					handleChange(filtered[0]);
					if (searchComponent.apiSearch) {
						return filtered;
					}
					return options;
				};

				// Case 1: Use form value if present and no user input
				if (formData && !inputValue) {
					const options = searchComponent.apiSearch
						? await searchComponent.handleInputChange(formData.label)
						: toLovOptions(await searchComponent.init());
					return processOptions(options, formData.label);
				}

				// Case 1.1: User input present but field not focused (auto fill from browser)
				if (inputValue && !isFocus) {
					const options = await searchComponent.handleInputChange(inputValue);
					setHandleMenu(searchComponent?.apiSearch ? false : undefined);
					processOptions(options, inputValue);
					return options ?? [];
				}

				// Case 2: API search with user input
				if (searchComponent.apiSearch) {
					setSearchResultValue(undefined);
					const options = await searchComponent.handleInputChange(inputValue).then((res) => {
						const result: SearchFieldOption[] = [];
						res?.forEach((item) => {
							result.push(item);
						});
						return result;
					});
					return options ?? [];
				}

				// Case 3: Local search with user input
				const options = await searchComponent.init();
				const lovOptions = toLovOptions(options);
				if (lovOptions.length > 0) {
					return filterValues(inputValue, lovOptions);
				}

				return [];
			},
			[searchComponent, filterValues, searchResultValue, isFocus, isCascadeFlow, formState, name]
		);

		// 300 ms debounce on every API-backed search (default on); local-filter
		// searches always pass through immediately regardless of the prop.
		const debouncedApiHandler = useMemo(() => debounce(handleAsyncOptions, 300), [handleAsyncOptions]);
		useEffect(() => () => debouncedApiHandler.cancel(), [debouncedApiHandler]);

		const debouncedHandleAsyncOptions = useCallback(
			(inputValue: string): Promise<SearchFieldOption[]> =>
				searchComponent?.apiSearch && debounced
					? debouncedApiHandler(inputValue)
					: handleAsyncOptions(inputValue),
			[handleAsyncOptions, debouncedApiHandler, searchComponent?.apiSearch, debounced]
		);

		const triggerCascade = useCallback(
			async (nextQuery: string) => {
				keepMenuOpenAfterSelectRef.current = true;
				setIsCascadeFlow(true);
				setSearchResultValue(undefined);
				setHandleMenu(true);
				setInputValue(nextQuery);

				const nextOptions = await handleAsyncOptions(nextQuery);
				setPrefetchedOptions(nextOptions);
				setAsyncKey((value) => value + 1);

				requestAnimationFrame(() => {
					selectRef.current?.getInputElement()?.focus();
				});
			},
			[handleAsyncOptions]
		);

		const applySecondarySearch = useCallback(
			async (modifiedOption: SearchFieldOption) => {
				if (
					!searchComponent?.secondarySearchConfiguration ||
					Object.keys(searchComponent.secondarySearchConfiguration).length === 0
				) {
					return;
				}

				const outputFields = searchComponent.secondarySearchConfiguration.outputFields?.items ?? [];
				const outputFieldNames = outputFields
					.map((config) => config.content.uniqueIdentifier?.contentName)
					.filter((contentName): contentName is string => !!contentName);

				// Mark secondary output fields as invalid while the SPD lookup is pending.
				for (const contentName of outputFieldNames) {
					const { onChange } = register(contentName, { required: true });
					await onChange({
						target: {
							name: contentName,
							value: undefined,
						},
					});
				}

				if (outputFieldNames.length > 0) {
					await trigger?.(outputFieldNames);
				}

				const kvhx = await searchComponent.handleSecondaryChange(modifiedOption, register);

				if (!searchComponent.searchConfiguration) {
					return;
				}

				const staticFieldItems = pageContext?.metaArea?.items?.[0]?.content?.staticFields?.items ?? [];

				const spdBody = {
					name: searchComponent.secondarySearchConfiguration.spdSearchConfigurationName,
					params: (searchComponent.secondarySearchConfiguration.inputFields?.items ?? []).map((item) => {
						const inputUid = item.content.uniqueIdentifier?.uid;
						const matchedDefaultValue = staticFieldItems.find(
							(staticItem) => staticItem?.content?.uniqueIdentifier?.uid === inputUid
						)?.content?.defaultValue;

						return {
							param_name: item.content.param || '',
							param_value: matchedDefaultValue || kvhx || '',
						};
					}),
				};

				const spdSearch = new SpdSearch(searchComponent.secondarySearchConfiguration, pageContext, spdBody);

				const spdOptions = await spdSearch.init().catch(() => undefined);

				// Keep the form invalid if the secondary API fails or does not return object data.
				if (!spdOptions || Array.isArray(spdOptions)) {
					return;
				}

				if (kvhx) {
					const date = new Date();
					const year = date.getFullYear();
					spdOptions['BUILDING_AGE'] = year - Number.parseInt(spdOptions['YEAR_CONSTRUCTION'], 10);
				}

				for (const config of outputFields) {
					const contentName = config.content.uniqueIdentifier?.contentName;
					const param = config.content.param ?? '';
					const outputValue = spdOptions[param];
					if (!contentName) {
						continue;
					}
					// Missing API values leave the required field empty, so the CTA stays disabled.
					if (outputValue === undefined || outputValue === null) {
						continue;
					}

					const { onChange } = register(contentName, { required: true });
					await onChange({
						target: {
							name: contentName,
							value: {
								id: contentName?.toString(),
								value: outputValue,
								label: param,
								type: contentType,
							},
						},
					});
				}

				if (outputFieldNames.length > 0) {
					await trigger?.(outputFieldNames);
				}
			},
			[searchComponent, register, trigger, pageContext, contentType]
		);

		// Handle value change (select option)
		const handleChange = useCallback(
			async (option: SearchFieldOption, actionTypes?: SelectActionType, focus = true) => {
				if (!searchComponent || !option) return;

				const selectionResult = searchComponent.handleChange
					? await searchComponent.handleChange(option, register)
					: { option, continueSearch: false };

				if (selectionResult.continueSearch && selectionResult.nextQuery) {
					const nextQuery = selectionResult.nextQuery.trim();
					if (!nextQuery) {
						return;
					}

					await triggerCascade(nextQuery);
					return;
				}

				// Handle focus on non safari autofill case
				if (focus) {
					const input = selectRef.current?.getInputElement();

					if (input) {
						// refocus
						setTimeout(() => {
							input.focus();
						}, 100);
					}
				}

				// Prevents firing onChange multiple times if not Safari autofill
				setIsTouched(true);

				// const modifiedOption = searchComponent.handleChange
				// 	? await searchComponent.handleChange(option)
				// 	: option;

				const modifiedOption = selectionResult.option;
				suppressNextLoadRef.current = true;
				setIsCascadeFlow(false);
				setInputValue(modifiedOption.label);
				setPrefetchedOptions(undefined);
				keepMenuOpenAfterSelectRef.current = false;
				await applySecondarySearch(modifiedOption);

				// Update form value
				onChange({
					target: {
						name,
						value: {
							id: name,
							label: modifiedOption.label,
							value: modifiedOption.value,
							inputLabel: placeholderText,
							type: contentType,
							stepTitle: props.stepTitle,
						},
					},
				});
				submitStep?.();

				// Update search result value so UI updates with correct value
				setSearchResultValue([modifiedOption]);
			},
			[
				searchComponent,
				register,
				onChange,
				name,
				placeholderText,
				contentType,
				props.stepTitle,
				submitStep,
				triggerCascade,
				applySecondarySearch,
			]
		);

		const handleInputChange = (nextInputValue: string, actionMeta?: InputActionMeta) => {
			if (actionMeta?.action === 'input-blur' && actionMeta.prevInputValue && !isTouched)
				fixAutofillOnSafari(actionMeta);

			console.log('Input changed:', nextInputValue, actionMeta);

			if (actionMeta?.action === 'input-change') {
				setInputValue(nextInputValue);
				setPrefetchedOptions(undefined);
			}

			if (actionMeta?.action === 'set-value' && isCascadeFlow) {
				return nextInputValue; // CHANGED: Return the actual input value, not stale state
			}

			// Control menu open state for API search
			if (searchComponent?.apiSearch) {
				setHandleMenu(nextInputValue?.trim().length >= searchComponent.minInputLength);
			} else {
				setHandleMenu(true);
			}

			return nextInputValue;
		};

		const fixAutofillOnSafari = (actionMeta: InputActionMeta) => {
			handleAsyncOptions(actionMeta.prevInputValue).then((options) => {
				options.length > 0 && setSearchResultValue([options[0]]);
				options.length > 0 && handleChange(options[0], null, false);
			});
		};

		const handleFocusChange = () => {
			setHandleMenu(true);
			setIsFocus(true);
		};

		type IconWithoutTheme = {
			iconProp: Omit<iconProp['iconProp'], 'themeName'>;
		};

		const getIcon = (): IconWithoutTheme => {
			if (icon) {
				return {
					iconProp: {
						icon: icon.icon,
						width: icon.width,
						height: icon.height,
					},
				};
			}
			return {
				iconProp: {
					icon: 'search',
				},
			};
		};

		return (
			renderField && (
				<SelectField<SearchFieldOption, false>
					key={asyncKey}
					{...props}
					themeName={themeName}
					isAsync
					icon={getIcon()}
					value={searchResultValue}
					inputValue={inputValue}
					asyncOptions={debouncedHandleAsyncOptions}
					defaultOptions={prefetchedOptions ?? true}
					filterOptions={searchComponent?.apiSearch}
					controlShouldRenderValue={!isCascadeFlow}
					onChange={handleChange}
					onInputChange={handleInputChange}
					onFocus={handleFocusChange}
					errorMessage={props.errorMessage ?? 'Vælg en værdi'}
					iconPosition={iconPosition}
					menuIsOpen={handleMenu}
					openMenuOnFocus
					closeMenuOnSelect={false}
					blurInputOnSelect={false}
					onMenuOpen={() => {
						setHandleMenu(true);
					}}
					onMenuClose={() => {
						if (keepMenuOpenAfterSelectRef.current) {
							keepMenuOpenAfterSelectRef.current = false;
							setHandleMenu(true);
							requestAnimationFrame(() => {
								selectRef.current?.getInputElement()?.focus();
							});
							return;
						}

						setHandleMenu(false);
						setIsCascadeFlow(false);
					}}
					onBlur={() => {
						setIsFocus(false);
					}}
					autoComplete={autocomplete}
					ref={selectRef}
				/>
			)
		);
	}
);

SearchFieldComponent.displayName = 'SearchFieldComponent';
